import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Eye, Sparkles } from "lucide-react";
import { useResource } from "../../hooks/useResource.js";
import { DataTable } from "../../components/data-table/DataTable.jsx";
import { Pagination } from "../../components/data-table/Pagination.jsx";
import { FilterBar } from "../../components/data-table/FilterBar.jsx";
import { Button } from "../../components/ui/Button.jsx";
import { Badge } from "../../components/ui/Badge.jsx";
import { Select } from "../../components/ui/Select.jsx";
import { RowActions } from "../../components/ui/RowActions.jsx";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog.jsx";
import { ImageThumbnail } from "../../components/upload/ImageUpload.jsx";

export default function DesignsListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [storey, setStorey] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState({ field: "sortOrder", dir: "asc" });
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    document.title = "Home Designs · Admin";
  }, []);

  const params = {
    page,
    limit: 12,
    sort: `${sort.field}:${sort.dir}`,
    ...(search && { search }),
    ...(storey && { storey }),
    ...(status && { status }),
  };

  const { list, remove } = useResource("designs", { toastLabel: "design" });
  const { data, isLoading } = list(params);

  // Reset to page 1 whenever filters change so the user doesn't end up on an
  // empty page after narrowing the result set.
  useEffect(() => {
    setPage(1);
  }, [search, storey, status]);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-accent">Catalogue</p>
          <h1 className="mt-1 font-display text-2xl text-text-primary">Home Designs</h1>
          <p className="mt-1 text-sm text-text-secondary">
            {data?.meta?.total ?? "—"} design{data?.meta?.total === 1 ? "" : "s"} in the catalogue
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/designs/smart-upload">
            <Button variant="secondary" leftIcon={<Sparkles className="h-4 w-4" />}>
              Smart Upload
            </Button>
          </Link>
          <Link to="/designs/new">
            <Button leftIcon={<Plus className="h-4 w-4" />}>New design</Button>
          </Link>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-border-subtle">
        <FilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search designs by name…"
          filters={
            <>
              <Select
                value={storey}
                onChange={(e) => setStorey(e.target.value)}
                className="min-w-[140px]"
              >
                <option value="">Any storey</option>
                <option value="single">Single</option>
                <option value="double">Double</option>
              </Select>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="min-w-[140px]"
              >
                <option value="">Any status</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </Select>
            </>
          }
        />

        <DataTable
          loading={isLoading}
          rows={data?.items ?? []}
          sort={sort}
          onSortChange={(field, dir) => setSort({ field, dir })}
          onRowClick={(row) => navigate(`/designs/${row._id}`)}
          columns={[
            {
              key: "name",
              label: "Design",
              sortable: true,
              render: (r) => (
                <div className="flex items-center gap-3">
                  {/* Prefer heroImage (the dedicated single hero) and fall
                      back to the first gallery shot. Without this, a
                      design that has only a hero image (no gallery yet)
                      shows the placeholder icon — looks identical across
                      every row, which is the "same stock image" report. */}
                  <ImageThumbnail src={r.heroImage || r.images?.[0]} alt={r.name} />
                  <div>
                    <p className="font-medium text-text-primary">{r.name}</p>
                    <p className="text-xs text-text-muted">{r.slug}</p>
                  </div>
                </div>
              ),
            },
            {
              key: "specs",
              label: "Specs",
              render: (r) => (
                <span className="text-text-secondary">
                  {r.bedrooms} bed · {r.bathrooms} bath · {r.areaSq} sq · {r.storey}
                </span>
              ),
            },
            {
              key: "status",
              label: "Status",
              sortable: false,
              render: (r) => (
                <div className="flex items-center gap-1.5">
                  <Badge tone={r.status === "published" ? "success" : "default"}>{r.status}</Badge>
                  {r.isFeatured && <Badge tone="accent">featured</Badge>}
                </div>
              ),
            },
            {
              key: "areaSq",
              label: "Area",
              sortable: true,
              render: (r) => <span className="text-text-secondary">{r.areaSq ?? "—"} sq</span>,
            },
          ]}
          actions={(row) => (
            <RowActions
              onEdit={() => navigate(`/designs/${row._id}`)}
              onDelete={() => setConfirmDelete(row)}
              extras={
                row.status === "published" && (
                  <a
                    href={`${import.meta.env.VITE_PUBLIC_SITE_URL ?? "http://localhost:5173"}/home-designs/${row.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded p-1.5 text-text-secondary hover:bg-bg-elevated-2 hover:text-accent"
                    title="View on public site"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Eye className="h-4 w-4" />
                  </a>
                )
              }
            />
          )}
          empty={{
            title: search || storey || status ? "No matches" : "No designs yet",
            description:
              search || storey || status
                ? "Try clearing the filters above."
                : "Create your first home design to populate the catalogue.",
          }}
        />
        <Pagination meta={data?.meta} onChange={setPage} />
      </div>

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        title={`Delete "${confirmDelete?.name}"?`}
        description="This removes the design from the catalogue. Linked house & land packages and display homes will keep their references but will no longer resolve."
        confirmLabel="Delete design"
        loading={remove.isPending}
        onConfirm={() => {
          remove.mutate(confirmDelete._id, {
            onSuccess: () => setConfirmDelete(null),
          });
        }}
      />
    </div>
  );
}
